<script setup>

import Validation from "@/classes/global/Validation";
import {ref, watch, computed} from 'vue'

const labelToLeft = computed(() => setLabelToLeft());
const emits = defineEmits(
    ['update:modelValue', 'update:validationFail', 'keydown', 'click', 'mouseleave', 'wheel', 'blur', 'focusout', 'cleared', 'focus'])

const props = defineProps({

  modelValue: {},

  clearable: {type: Boolean, default: false},
  cssStyle: {type: String},
  validationFail: {type: Object, default: {}},
  validationText: {type: Array, default: ['Required']},
  disable: {type: Boolean, default: false},
  readonly: {type: Boolean, default: false},
  placeholder: {type: String},
  maxLength: {type: Number, default: 80},
  width: {type: Number, default: 450},
  label: {type: String, default: ''},
  labelTop: {type: Boolean, default: false},
  requiredField: {type: Boolean, default: false},
  labelWidth: {type: Number, default: 105},
  title: String,
  highlightRequiredBackground: false
})

const inputRef = ref()
const labelWidthLocal = ref(props.labelWidth + 'px');

const altTitle = computed(() => {

  if(props.title) return props.title;

  return props.modelValue;

})

function handleEnter() {
  inputRef.value.blur();
}

function handleClear() {

  if(!props.disable) {
    emits('cleared');
    emits('update:modelValue', '');
    emits('update:validationFail', new Validation());
  }

};

function performEmits() {
  emits('update:validationFail', new Validation());
}

function setLabelToLeft() {
  if (!props.labelTop) {
    return 'flex'
  }
  return '';
};

watch(() => props.modelValue, () => {

  if(props.modelValue) emits('update:validationFail', new Validation());
})

</script>

<template>
  <div id="Vue-3-component-text-input" class="input-wrapper" :style="{display: labelToLeft}">
    <div v-show="label.length > 0"
         :class="{'label-left' : labelToLeft === 'flex', 'global-required-field' : requiredField}"
         :title="label"
         style="font-size: 12px; margin-top: -16px;">{{ label }}
    </div>
    <div class="input-container" :style="{width: width + 'px'}">
      <input onselectstart="return false"
             ref="inputRef"
             :placeholder="placeholder"
             type="search"
             class="input-main-style"
             id="standard-input"
             :class="{ 'input-default': !validationFail.value, 'input-validation-fail': validationFail.value,
              'required-background' : props.highlightRequiredBackground }"
             :style="cssStyle + '!important'"
             :readonly="readonly"
             :disabled="disable"
             :title="altTitle"
             :value="modelValue"
             :maxlength="maxLength"
             autocomplete="off"
             @keydown="$emit('keydown', $event)"
             @keydown.enter="handleEnter"
             @click="$emit('click', $event)"
             @blur="$emit('blur', $event)"
             @focusout="$emit('focusout', $event)"
             @focus="$emit('focus', $event)"
             @keypress="performEmits"
             @mouseleave="$emit('mouseleave', $event)"
             @wheel="$emit('wheel', $event)"
             @input="(event) => $emit('update:modelValue', event.target.value)"/>
      <div v-if="clearable && !disable" class="input-clear-container" @click="handleClear">
        <div class="input-cross">+</div>
      </div>
      <slot></slot>
      <div v-if="validationFail.value" class="input-validation-message">
        {{ validationText[validationFail.messageNumber] }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.input-wrapper {
  //z-index: 999;

}

.input-container {
  position: relative;
}

.input-main-style {
  border-radius: 4px;
  /*margin: 0px 0 6px 0px;*/
  height: 26px;
  width: 100%;
  padding-left: 5px;
  font-family: inherit;
  text-overflow: ellipsis;
}

.input-validation-message {
  position: absolute;
  font-family: Arial;
  font-size: 10px;
  color: var(--required-field);
  top: 18px;
  background: #fdfdfd;
  width: fit-content;
  padding: 0 3px 0 3px;
  line-height: normal;
  border-radius: 6px;
}

/* Dynamic CSS from bound HTML*/

.required-background {
  background-color: var(--required-field-bg);
}

.label-left {
  padding: 5px 5px 0 0;
  text-align: left;
  width: v-bind(labelWidthLocal);
  color: #222222;
  margin-top:0!important;
}

.input-default {
  border: 1px solid #c3c3c3;
  font-family: inherit;
}

.input-default:hover {
  border-color: #4c7bae;
  box-shadow: 0 0 1px #719ECE;
}

.input-validation-fail {
  border: 1px dotted var(--required-field) !important;
}

/*Global CSS */

input:focus {
  outline: none;
  border-color: #4c7bae;
  box-shadow: 0 0 2px #719ECE;
}

input::placeholder {
  color: #b3b3b3;
  font-size: 11px;
  font-style: italic;
}

input[type=search]:disabled {
  background-color: #f8f8f8 !important;
  opacity: 1 !important;
  border-color: #e2e2e2;
}

</style>
